import React, { ReactNode, useMemo } from 'react'

import { SxProps, Theme } from '@mui/material'

import { OptionProps } from 'components_new/molecules/OptionsMenu'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon, { IconName } from 'components_new/atoms/Icon'
import Tab from 'components_new/atoms/Tab'
import Tabs from 'components_new/atoms/Tabs'
import Tooltip from 'components_new/atoms/Tooltip'

import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'

export type TabSwitchOption = {
  disabled?: boolean
  disabledTooltip?: string
  icon?: {
    bgcolor?: string
    name: IconName
    tooltip?: ReactNode
  }
  label: string | ReactNode
  menuOptions?: OptionProps[]
  number?: number
  sx?: SxProps<Theme>
  value: any
}

interface TabSwitchProps {
  color?: 'primary' | 'secondary' | 'brand'
  maxHeight?: string | number
  noGutter?: boolean
  onChange: (value: any) => void
  orientation?: 'horizontal' | 'vertical'
  options: TabSwitchOption[]
  variant?: 'scrollable' | 'fullWidth' | 'standard'
  value?: any
  sx?: SxProps
}

const TabSwitch = React.forwardRef((props: TabSwitchProps, ref: any) => {
  const {
    color,
    onChange,
    maxHeight,
    noGutter,
    orientation,
    options,
    value,
    variant = 'scrollable',
    sx,
    ...rest
  } = props

  const selectedValue = useMemo(() => {
    if (value) {
      const chosen = options.findIndex((option) => option.value === props.value)

      if (chosen !== -1) {
        return chosen
      }
    }

    return 0 // Defaults to first tab.
  }, [value, options])

  return (
    <Box ref={ref} sx={{ minWidth: 0, ...sx }}>
      <Tabs
        {...rest}
        color={color}
        value={selectedValue}
        onChange={(event, value) => onChange(props.options[value].value)}
        orientation={orientation}
        variant={variant}
        scrollButtons={true}
        allowScrollButtonsMobile={true}
        TabScrollButtonProps={{
          sx: {
            color: 'text.secondary',
            '&.Mui-disabled': {
              width: 0,
              height: 0
            },
            overflow: 'hidden',
            transition: 'width 0.5s'
          }
        }}
        sx={{
          minHeight: noGutter ? 0 : undefined,
          maxHeight: maxHeight ?? undefined
        }}
      >
        {options.map((option) => (
          <Tab
            disabled={option.disabled}
            endIcon={
              option.number != undefined ? (
                <Chip
                  color={value === option.value ? color : 'default'}
                  disabled={value !== option.value}
                  label={option.number}
                  size="small"
                />
              ) : option.menuOptions ? (
                <OptionsMenuButton
                  buttonComponent="div"
                  disabled={option.disabled}
                  iconName="ExpandMore"
                  options={option.menuOptions}
                  size="xsmall"
                  sx={{ ml: 0.5 }}
                />
              ) : undefined
            }
            label={
              option.disabled && option.disabledTooltip ? (
                <Tooltip title={option.disabledTooltip}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {option.label}
                  </Box>
                </Tooltip>
              ) : (
                option.label
              )
            }
            key={option.value}
            startIcon={
              option.icon ? (
                <Tooltip enterDelay={500} title={option.icon.tooltip}>
                  <Box
                    sx={{
                      bgcolor: option.icon.bgcolor ?? 'background.overlay',
                      borderRadius: '50%',
                      cursor: 'default',
                      height: 22,
                      width: 22,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      verticalAlign: 'middle',
                      textAlign: 'center'
                    }}
                  >
                    <Icon name={option.icon.name} sx={{ fontSize: 12 }} />
                  </Box>
                </Tooltip>
              ) : undefined
            }
            sx={{
              ...option.sx,
              minHeight: noGutter || orientation === 'vertical' ? 0 : undefined,
              maxHeight: maxHeight ?? undefined,
              pointerEvents:
                option.disabled && option.disabledTooltip
                  ? 'all!important'
                  : undefined,
              textAlign: orientation === 'vertical' ? 'left' : undefined,
              justifyContent:
                orientation === 'vertical' ? 'space-between' : undefined
            }}
          />
        ))}
      </Tabs>
    </Box>
  )
})

TabSwitch.displayName = 'TabSwitch'
export default TabSwitch
